import { AxiosError, AxiosResponse } from 'axios';
import HttpClientWithRetry from '../http/client/HttpClientWithRetry';
import { AppointmentType } from '../firebase/firestore/documents/appointmentAvaiability';
import { GroupEvent } from '../firebase/firestore/documents/groupEvent';
import {
  BusinessPayloadT,
  BusinessPositionQueryParamsT,
  BusinessPositionResponseT,
  BusinessQueryParamsT,
  RecruiterAvailabilityResponseT,
  RecruiterResponseT,
} from '../modules/calendar/types';
import { CandidateResponseT } from '../firebase/firestore/documents/candidate';

export class CalendarController {
  constructor(private readonly httpClient: HttpClientWithRetry) {}

  async saveSchedule(
    fromDate: Date,
    toDate: Date,
    appointmentTypes: AppointmentType[],
    bookingCapacity: number,
    adhoc?: boolean,
    title?: string,
    location?: string,
    candidateId?: string,
    collaborators?: string[],
    selectedTimeZoneString?: string,
    selectedTimeZoneOffset?: string,
  ): Promise<AxiosResponse | undefined> {
    const path = '/api/v1/appointmentAvailabilities';
    const body = {
      fromDate,
      toDate,
      appointmentTypes,
      bookingCapacity,
      adhoc,
      title,
      location,
      candidateId,
      collaborators,
      selectedTimeZoneString,
      selectedTimeZoneOffset,
    };
    return this.httpClient
      .post(path, body, {
        /* istanbul ignore next */
        shouldBailRerequest(error) {
          if (error.response?.status === 500) {
            return true;
          }
          return false;
        },
      })
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async updateSchedule(id: string, payload: any) {
    const path = `/api/v1/appointmentAvailabilities/${id}`;
    await this.httpClient.put(path, payload);
  }

  async deleteSchedule(id: string) {
    const path = `/api/v1/appointmentAvailabilities/${id}`;
    await this.httpClient.delete(path);
  }

  async saveEvent(
    accountId: string,
    groupEvent: GroupEvent,
    candidates: string[],
    recruiters: string[],
  ): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/groupEvent/${accountId}/create`;
    const body = { groupEvent, candidates, recruiters };
    return this.httpClient
      .post(path, body)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async updateEvent(
    groupEventId: string,
    groupEvent: GroupEvent,
    candidates: string[],
    recruiters: string[],
  ): Promise<AxiosResponse | undefined> {
    const path = `api/v1/groupEvent/${groupEventId}/update`;
    const body = { groupEventId, groupEvent, candidates, recruiters };
    return this.httpClient
      .put(path, body)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async getEventToken(groupEventId: string): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/groupEvent/${groupEventId}/token`;
    return this.httpClient
      .get(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async deleteEvent(groupEventId: string) {
    const path = `api/v1/groupEvent/${groupEventId}`;
    await this.httpClient.delete(path);
  }

  async cancelIndividualBooking(positionId: string, seekerId: string, reason: string): Promise<void> {
    const path = `/api/v1/position/${positionId}/assignedRecruiters/cancelAppointment`;
    const body = { seekerId, reason };
    await this.httpClient.put(path, body);
  }

  async fetchBusinesses(params: BusinessQueryParamsT) {
    const path = '/api/v5/businesses';
    return this.httpClient.get<BusinessPayloadT>(path, { params });
  }

  async fetchBusinessPositions(businessId: string, params: BusinessPositionQueryParamsT) {
    const path = `/api/v1/position/business/${businessId}/positionList`;
    return this.httpClient.get<BusinessPositionResponseT>(path, { params });
  }

  async fetchAllRecruiters(accountId: string) {
    const path = `/api/v1/account/${accountId}/recruiters`;
    return this.httpClient.get<RecruiterResponseT>(path);
  }

  async fetchAvailabilityByRecruiters(payload: { userIds: string[]; from: Date; to: Date }) {
    const path = `/api/v1/appointmentAvailabilities/query`;
    return this.httpClient.post<RecruiterAvailabilityResponseT>(path, payload);
  }

  async fetchCandidates(payload: { from: Date; to: Date; isCalendarBased: boolean }) {
    const path = `/api/v1/appointmentAvailabilities/getAppointmentAvailabilitiesOfCandidate`;
    return this.httpClient.post<CandidateResponseT>(path, payload);
  }
}
